import { forwardRef, ForwardRefRenderFunction, useState, useEffect, useMemo } from "react";
import { FormikProps, useFormikContext } from "formik";
//#region redux
import { useDispatch } from "react-redux";
import { displayDialog, closeDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { AutoCompleteExtraProps, FormHandles, NumberFormatFieldExtraProps } from "models/propTypes/genericFormProps";
import { MessageBubbleDetails } from "models/propTypes/messagePropTypes";
import { DimensionEnums, FormEnums, DialogEnums, StyleEnums } from "models/enums";
import { CommonConstants, EmployeeConstants, FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { SettingsFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelWintSalary } from "wint-api-client/dist/types";
import { EmployeeFormProps } from "models/propTypes/settingsPropTypes";
import { TranslationKeys } from "translations";
import { EmployeeType } from "models/enums/employee";
//#endregion
//#region components
import { GenericForm } from "components/common";
import useEmployeeHelpers from "../useEmployeeHelpers";
import useConcurrentVacationWarning from "../useConcurrentVacationWarning";
import { AddSupportCaseLink } from "components/support";
import Schedule from "../schedule";
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";
import SalaryTypeSelector from "./SalaryTypeSelector";
import UserTypeSelector from "./UserTypeSelector";
import ScheduledChanges from "./ScheduledChanges";
import useScheduledChanges from "./useScheduledChanges";
import {
  getStandardWorkTimeBaseline,
  getWorkRateBaseline,
  standardWorkTimeChangedButWorkRateUnchanged,
} from "./scheduleWorkTimeValidation";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { EnumHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import { CustomerTerminationReasons, terminationReasonLabel } from "utils/helpers/terminationReasonHelper";

// Local type extension for temporary vacation compensation field
type EmployeeSettingsWithTemp = SettingsFormTypes.EmployeeSettings & {
  tempVacationCompensation?: number;
  hourlySalary?: number;
  taxAdjustment?: number;
  scheduledChanges?: any;
};

const getTerminationReasonItems = (stored?: WintSuperkollAPIModel.EmploymentTerminationReason) => {
  const items = CustomerTerminationReasons.map((reason) => ({
    id: reason,
    value: terminationReasonLabel(reason),
  }));

  if (
    stored != null &&
    stored !== WintSuperkollAPIModel.EmploymentTerminationReason.Undefined &&
    !CustomerTerminationReasons.includes(stored)
  ) {
    items.unshift({
      id: stored,
      value: terminationReasonLabel(stored),
    });
  }

  return items;
};

// Wrapper component to access current form values for Schedule
const ScheduleWithFormValues: React.FC<any> = (props) => {
  const { values } = useFormikContext<SettingsFormTypes.EmployeeSettings>();
  const {
    fieldName = "schedule",
    employmentRateField = "employmentRate",
    usualWorkHoursField = "usualWorkHours",
    scheduleViewMode,
    hasScheduledChanges,
    errors,
    touched,
    ...restProps
  } = props;

  // Calculate disabled state dynamically based on actual Formik values
  const hasFutureSchedule = values.wintSalarySpecificSettings?.schedule?.futureSchedule != null;
  const isDisabled = scheduleViewMode === "current" && hasFutureSchedule;

  // errors comes live from the GenericForm CUSTOM-field pipeline; shown without a touched gate
  // so grid, Ordinarie arbetstid and sysselsättningsgrad changes all red-flag immediately
  const showError = typeof errors === "string";

  return (
    <Schedule
      {...restProps}
      name={fieldName}
      value={values[fieldName as keyof SettingsFormTypes.EmployeeSettings]}
      usualWorkHours={(values[usualWorkHoursField as keyof SettingsFormTypes.EmployeeSettings] as number) || 40}
      employmentRate={(values[employmentRateField as keyof SettingsFormTypes.EmployeeSettings] as number) || 100}
      disabled={isDisabled}
      errorText={showError ? LocalizationHelperInstance.getFormattedMessage(errors) : undefined}
    />
  );
};

// Wrapper component for ScheduledChanges
const ScheduledChangesWrapper: React.FC<{
  isWintSalarySettingsAvailable: boolean;
  scheduleViewMode: "current" | "scheduled";
  onScheduleViewModeChange: (mode: "current" | "scheduled") => void;
  onScheduledChangesDetected?: (hasChanges: boolean) => void;
}> = ({ isWintSalarySettingsAvailable, scheduleViewMode, onScheduleViewModeChange, onScheduledChangesDetected }) => {
  const dispatch = useDispatch();
  const { values, setFieldValue } = useFormikContext<SettingsFormTypes.EmployeeSettings>();

  const { scheduledChanges, viewMode, setViewMode } = useScheduledChanges({
    currentValues: values,
    isWintSalarySettingsAvailable,
  });

  // Notify parent when scheduled changes are detected
  useEffect(() => {
    onScheduledChangesDetected?.(scheduledChanges.hasScheduledChanges);
  }, [scheduledChanges.hasScheduledChanges, onScheduledChangesDetected]);

  // This wrapper unmounts with its section once the future schedule is gone (a re-hire save drops it), and
  // it is the only reporter, so the parent would otherwise keep believing a scheduled change exists.
  useEffect(() => () => onScheduledChangesDetected?.(false), [onScheduledChangesDetected]);

  const handleTabChange = (newMode: "current" | "scheduled") => {
    setViewMode(newMode);
    onScheduleViewModeChange(newMode);
  };

  const handleCancelScheduledChanges = () => {
    dispatch(
      displayDialog({
        isOpen: false,
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.CancelScheduledChangesTitle),
        titleIcon: IconDefinitionConstants.WTrashAlt,
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.RemoveScheduling),
            onClick: async () => {
              if (values.wintSalarySpecificSettings?.schedule) {
                const updatedSchedule = { ...values.wintSalarySpecificSettings.schedule };
                delete updatedSchedule.futureSchedule;
                await setFieldValue("wintSalarySpecificSettings.schedule", updatedSchedule);
              }

              await setFieldValue("futureSchedule", undefined);
              await setFieldValue("futureEmploymentRate", undefined);
              await setFieldValue("futureUsualWorkHours", undefined);

              setViewMode("current");
              onScheduleViewModeChange("current");

              // Close the dialog
              dispatch(closeDialog());
            },
            type: StyleEnums.DisplayType.Danger,
          },
        ],
        dialogType: DialogEnums.DialogType.Message,
        children: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Settings.CancelScheduledChangesDescription
        ),
        type: StyleEnums.DisplayType.Danger,
      })
    );
  };

  return (
    <ScheduledChanges
      scheduledChanges={scheduledChanges}
      currentTab={viewMode}
      onTabChange={handleTabChange}
      onCancelScheduledChanges={handleCancelScheduledChanges}
    />
  );
};

const EmployeeForm: ForwardRefRenderFunction<FormHandles, EmployeeFormProps> = (props, ref) => {
  const {
    handleSpeedDial,
    onPermissionChange,
    onSalaryToggle,
    onSalaryChange,
    salaryAvailable,
    includeCostCenterDimensionToSalary,
    defaultValues,
    onScheduleViewModeChange,
    onEmploymentStartDateChange,
  } = props;
  const { monthlySalary, employmentRate } = defaultValues as SettingsFormTypes.EmployeeSettings;
  const typedDefaults = defaultValues as SettingsFormTypes.EmployeeSettings;

  const { canCreate } = useEmployeeHelpers();
  const { isWintSalarySettingsAvailable } = useSalaryPermissions();
  const checkConcurrentVacationYear = useConcurrentVacationWarning(
    props.employeeId,
    typedDefaults.employmentEndDate,
    salaryAvailable && isWintSalarySettingsAvailable
  );
  const [scheduleViewMode, setScheduleViewMode] = useState<"current" | "scheduled">("current");
  const [hasScheduledChanges, setHasScheduledChanges] = useState<boolean>(false);
  const handleScheduleViewModeChange = (mode: "current" | "scheduled") => {
    setScheduleViewMode(mode);
    onScheduleViewModeChange(mode);
  };

  // The tab selector disappears with the scheduled change (a re-hire drops it on save), so a form left on
  // the scheduled tab would hide the current schedule and keep saving through the future-schedule branch.
  useEffect(() => {
    if (!hasScheduledChanges && scheduleViewMode === "scheduled") {
      setScheduleViewMode("current");
      onScheduleViewModeChange("current");
    }
  }, [hasScheduledChanges, scheduleViewMode, onScheduleViewModeChange]);

  const isWorkplaceOptional = (field: keyof SettingsFormTypes.EmployeeSettings) =>
    !props.isCreate && typedDefaults?.salaryActive && UtilsHelper.isEmpty(typedDefaults[field]);

  const getWorkplaceDescriptionText = (text: string, field: keyof SettingsFormTypes.EmployeeSettings) => {
    return (
      <>
        {LocalizationHelperInstance.getFormattedMessage(text)}{" "}
        {isWorkplaceOptional(field) && (
          <>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.WorkPlaceOptionalHint)}{" "}
            <a
              href="https://faq.wint.se/faq-fragor/uppdatera-tjanstestalle-for-anstallda"
              target="_blank"
              rel="noreferrer"
            >
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.WorkPlace).toLowerCase()}
            </a>
          </>
        )}
      </>
    );
  };

  const checkStartDateNotFirst = async (
    values: SettingsFormTypes.EmployeeSettings
  ): Promise<MessageBubbleDetails | undefined> => {
    if (!values.employmentStartDate) return undefined;
    const date = new Date(values.employmentStartDate);
    if (isNaN(date.getTime())) return undefined;
    const day = date.getDate();
    if (day === 1) return undefined;
    const month = LocalizationHelperInstance.getFormattedDate(date, { month: "long" });
    const reducedDays = day - 1;
    return {
      type: StyleEnums.DisplayType.Warning,
      message: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Settings.StartDateNotFirstWarning,
        undefined,
        { month, days: String(reducedDays) }
      ),
    };
  };

  const checkActualSalary = async (
    values: SettingsFormTypes.EmployeeSettings
  ): Promise<MessageBubbleDetails | undefined> => {
    const salary = values.monthlySalary;
    const rate = values.employmentRate;
    if (!salary || !rate || rate === 0 || rate >= 100) return undefined;
    const actualSalary = salary * (rate / 100);
    const formattedSalary = LocalizationHelperInstance.getFormattedNumber(actualSalary, 0, 0);
    return {
      type: StyleEnums.DisplayType.Info,
      message: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Settings.ActualSalaryHelpText,
        undefined,
        { salary: formattedSalary, rate: String(rate) }
      ),
    };
  };

  const checkTerminationReasonLocked = async (
    values: SettingsFormTypes.EmployeeSettings
  ): Promise<MessageBubbleDetails | undefined> =>
    values.canChangeTerminationReason === false
      ? {
          type: StyleEnums.DisplayType.Info,
          message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.TerminationReasonLocked),
        }
      : undefined;

  const getUsualWorkHoursChangedWarning = (): MessageBubbleDetails => ({
    type: StyleEnums.DisplayType.Warning,
    message: LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Settings.UsualWorkHoursChangedWithoutEmploymentRateWarning
    ),
  });

  // Fires whenever the norm differs from the persisted schedule while the rate does not —
  // both when Ordinarie arbetstid is edited directly and when a grid change is absorbed into it
  const checkUsualWorkHoursChangedWithoutRate = async (
    values: SettingsFormTypes.EmployeeSettings
  ): Promise<MessageBubbleDetails | undefined> => {
    const schedule = values.wintSalarySpecificSettings?.schedule;
    return standardWorkTimeChangedButWorkRateUnchanged(
      values.usualWorkHours,
      values.employmentRate,
      getStandardWorkTimeBaseline(schedule),
      getWorkRateBaseline(schedule)
    )
      ? getUsualWorkHoursChangedWarning()
      : undefined;
  };

  const checkFutureUsualWorkHoursChangedWithoutRate = async (
    values: SettingsFormTypes.EmployeeSettings
  ): Promise<MessageBubbleDetails | undefined> => {
    const futureSchedule = values.wintSalarySpecificSettings?.schedule?.futureSchedule;
    return standardWorkTimeChangedButWorkRateUnchanged(
      values.futureUsualWorkHours,
      values.futureEmploymentRate,
      futureSchedule?.standardWorkTime,
      futureSchedule?.workRate
    )
      ? getUsualWorkHoursChangedWarning()
      : undefined;
  };

  // Wrap fields in useMemo to recalculate when scheduleViewMode changes
  const formFields = useMemo(() => {
    return [
      {
        label: TranslationKeys.Settings.EmployeePersonalInformation,
        type: FormEnums.FormSectionType.WIZARDTAB,
        icon: IconDefinitionConstants.WPerson,
        className: cssExports.groupSection,
        hidden: !canCreate,
        navKey: EmployeeConstants.NavigationKey.General,
        innerFields: {
          name: {
            label: TranslationKeys.Common.Name,
            descriptionText: TranslationKeys.Settings.EmployeeNameDescription,
            type: FormEnums.FormFieldType.TEXT,
            disabled: !props.isCreate,
          },
          mail: {
            label: TranslationKeys.Settings.EPost,
            descriptionText: TranslationKeys.Settings.EPostDescription,
            type: FormEnums.FormFieldType.TEXT,
            disabled: !props.isCreate,
          },
          phone: {
            label: TranslationKeys.Settings.EmployeeTelephone,
            descriptionText: TranslationKeys.Settings.EmployeeTelephoneDescription,
            type: FormEnums.FormFieldType.TEXT,
          },
          personNumber: {
            label: TranslationKeys.Settings.PersonNumber,
            descriptionText: (
              <>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.EditPersonalNumber)}{" "}
                <AddSupportCaseLink />
              </>
            ),
            placeholder: TranslationKeys.Settings.PersonNumber,
            type: FormEnums.FormFieldType.NUMBER,
            disabled: props.hasPersonNumber,
            extraProps: {
              allowLeadingZeros: true,
              format: CommonConstants.PersonNumberMask,
              formattedValueReferenceFieldName: FormFieldConstants.Settings.EmployeeSettings.PersonNumberFormatted,
            } as NumberFormatFieldExtraProps,
          },
          incomingInvoiceReferenceNames: {
            label: TranslationKeys.Settings.EmployeeOtherReferenceName,
            descriptionText: TranslationKeys.Settings.EmployeeOtherReferenceNameDescription,
            type: FormEnums.FormFieldType.MULTIPLEINPUT,
            innerFields: {
              name: {
                label: TranslationKeys.Settings.EmployeeOtherReferenceName,
                type: FormEnums.FormFieldType.TEXT,
              },
            },
          },
          userTypeId: {
            label: TranslationKeys.Settings.UserType,
            type: FormEnums.FormFieldType.CUSTOM,
            hidden: !props.userTypesEnabled,
            customField: (
              <UserTypeSelector
                name={FormFieldConstants.Settings.EmployeeSettings.UserTypeId}
                enabledUserTypes={props.enabledUserTypes ?? []}
              />
            ),
          },
        },
      },
      {
        label: TranslationKeys.Settings.Rights,
        type: FormEnums.FormSectionType.WIZARDTAB,
        icon: IconDefinitionConstants.WLock,
        className: cssExports.groupSection,
        hidden: !props.isAdmin,
        navKey: EmployeeConstants.NavigationKey.Rights,
        innerFields: [
          {
            label: "",
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            inline: false,
            innerFields: {
              quickSelection: {
                label: "",
                type: FormEnums.FormFieldType.SELECTION,
                extraProps: {
                  items: EnumHelper.getNumericItemValues(EmployeeType).map((type) => ({
                    id: type,
                    value: LocalizationHelperInstance.getFormattedMessage(`Settings.EmployeeType_${type}`),
                  })),
                },
                onChangeHandler: handleSpeedDial,
              },
            },
          },
          {
            label: TranslationKeys.Settings.CustomerInvoices,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WSend,
            innerFields: {
              invoices: {
                label: TranslationKeys.Permission.Invoices,
                descriptionText: TranslationKeys.Permission.InvoiceDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              invoicesAdmin: {
                label: TranslationKeys.Permission.InvoicesAdmin,
                descriptionText: TranslationKeys.Permission.InvoiceAdminDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
            },
          },
          {
            label: TranslationKeys.Settings.SupplierInvoices,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WInvoiceSupplier,
            innerFields: {
              incomingInvoices: {
                label: TranslationKeys.Permission.IncomingInvoices,
                descriptionText: TranslationKeys.Permission.IncomingInvoicesDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              incomingInvoicesOverview: {
                label: TranslationKeys.Permission.IncomingInvoicesOverview,
                descriptionText: TranslationKeys.Permission.IncomingInvoicesOverviewDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              incomingInvoicesSignee: {
                label: TranslationKeys.Permission.IncomingInvoicesSignee,
                descriptionText: TranslationKeys.Permission.IncomingInvoicesSigneeDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              incomingInvoicesAdmin: {
                label: TranslationKeys.Permission.IncomingInvoicesAdmin,
                descriptionText: TranslationKeys.Permission.IncomingInvoicesAdminDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              paymentFileSignee: {
                label: TranslationKeys.Permission.PaymentFileSignee,
                descriptionText: TranslationKeys.Permission.PaymentFileSigneeDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
            },
          },
          {
            label: TranslationKeys.Receipt.Receipts,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WReceipt,
            innerFields: {
              receipts: {
                label: TranslationKeys.Permission.Receipts,
                descriptionText: TranslationKeys.Permission.ReceiptsDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              receiptsAdmin: {
                label: TranslationKeys.Permission.ReceiptsAdmin,
                descriptionText: TranslationKeys.Permission.ReceiptsAdminDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
            },
          },
          {
            label: TranslationKeys.Common.Accounting,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WList,
            innerFields: {
              reports: {
                label: TranslationKeys.Permission.Reports,
                descriptionText: TranslationKeys.Permission.ReportsDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              missingDocumentAdmin: {
                label: TranslationKeys.Permission.MissingDocumentAdmin,
                descriptionText: TranslationKeys.Permission.MissingDocumentAdminDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              uploadGenericDocuments: {
                label: TranslationKeys.Permission.UploadGenericDocuments,
                descriptionText: TranslationKeys.Permission.UploadGenericDocumentsDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
            },
          },
          {
            label: TranslationKeys.Common.Features,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WDashboard,
            innerFields: {
              documents: {
                label: TranslationKeys.Permission.Documents,
                descriptionText: TranslationKeys.Permission.DocumentsDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              agreement: {
                label: TranslationKeys.Permission.Agreement,
                descriptionText: TranslationKeys.Permission.AgreementDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              cashReconciliation: {
                label: TranslationKeys.Permission.CashReconciliation,
                descriptionText: TranslationKeys.Permission.CashReconciliationDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
            },
          },
          {
            label: TranslationKeys.Common.Salaries,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WMoney,
            innerFields: {
              salaryAdmin: {
                label: TranslationKeys.Permission.SalaryAdmin,
                descriptionText: TranslationKeys.Permission.SalaryAdminDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              deviationAdmin: {
                label: TranslationKeys.Permission.DeviationAdmin,
                descriptionText: TranslationKeys.Permission.DeviationAdminDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
            },
          },
          {
            label: TranslationKeys.Common.Payments,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WCosts,
            innerFields: {
              paymentSignee: {
                label: TranslationKeys.Permission.PaymentSignee,
                descriptionText: TranslationKeys.Permission.PaymentSigneeDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              stoppedPaymentAdmin: {
                label: TranslationKeys.Permission.StoppedPaymentAdmin,
                descriptionText: TranslationKeys.Permission.StoppedPaymentAdminDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
            },
          },
          {
            label: TranslationKeys.Common.WintSettings,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WLock,
            innerFields: {
              admin: {
                label: TranslationKeys.Permission.Admin,
                descriptionText: TranslationKeys.Permission.AdminDescription,
                type: FormEnums.FormFieldType.TOGGLE,
                onChangeHandler: onPermissionChange,
              },
              contact: {
                label: TranslationKeys.Permission.Contact,
                type: FormEnums.FormFieldType.TOGGLE,
                disabled: true,
                descriptionText: (
                  <>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Permission.ContactDescription)}{" "}
                    <AddSupportCaseLink />
                  </>
                ),
              },
              advisor: {
                label: TranslationKeys.Permission.Advisor,
                descriptionText: (
                  <>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Permission.AdvisorDescription)}{" "}
                    <AddSupportCaseLink />
                  </>
                ),
                type: FormEnums.FormFieldType.TOGGLE,
                disabled: true,
              },
              revisor: {
                label: TranslationKeys.Permission.Revisor,
                type: FormEnums.FormFieldType.TOGGLE,
                disabled: true,
                descriptionText: (
                  <>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Permission.RevisorDescription)}{" "}
                    <AddSupportCaseLink />
                  </>
                ),
              },
              auditor: {
                label: TranslationKeys.Permission.Auditor,
                type: FormEnums.FormFieldType.TOGGLE,
                disabled: true,
                descriptionText: (
                  <>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Permission.AuditorDescription)}{" "}
                    <AddSupportCaseLink />
                  </>
                ),
              },
              board: {
                label: TranslationKeys.Permission.Board,
                type: FormEnums.FormFieldType.TOGGLE,
                disabled: true,
                descriptionText: (
                  <>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Permission.BoardDescription)}{" "}
                    <AddSupportCaseLink />
                  </>
                ),
              },
            },
          },
        ],
      },
      {
        label: TranslationKeys.Settings.EmployeeSalaryInformation,
        type: FormEnums.FormSectionType.WIZARDTAB,
        icon: IconDefinitionConstants.WIncome2,
        className: cssExports.groupSection,
        navKey: EmployeeConstants.NavigationKey.Salary,
        innerFields: [
          {
            label: TranslationKeys.Settings.BasicSettings,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WInfo,
            innerFields: {
              salaryActive: {
                label: TranslationKeys.Settings.SalaryActive,
                type: FormEnums.FormFieldType.TOGGLE,
                hidden: !salaryAvailable || isWintSalarySettingsAvailable,
                onChangeHandler: onSalaryToggle,
              },
              employmentStartDate: {
                label: TranslationKeys.Settings.StartDate,
                descriptionText: props.canRehire ? (
                  TranslationKeys.Settings.RehireStartDateDescription
                ) : props.hasEmploymentStartDate ? (
                  <>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.EditStartDate)}{" "}
                    <AddSupportCaseLink />
                  </>
                ) : (
                  TranslationKeys.Settings.StartDateDescription
                ),
                type: FormEnums.FormFieldType.DATE,
                hidden: !salaryAvailable,
                disabled: isWintSalarySettingsAvailable && props.hasEmploymentStartDate && !props.canRehire,
                onChangeHandler: onEmploymentStartDateChange,
                customMessageHandler: checkStartDateNotFirst,
              },
              employmentEndDate: {
                label: TranslationKeys.Settings.EndDate,
                descriptionText: TranslationKeys.Settings.EndDateDescription,
                type: FormEnums.FormFieldType.DATE,
                hidden: (values: SettingsFormTypes.EmployeeSettings) =>
                  !salaryAvailable || (isWintSalarySettingsAvailable && !values.employmentStartDate),
                customMessageHandler: checkConcurrentVacationYear,
                onChangeHandler: (
                  _name: string,
                  value: any,
                  formikProps: FormikProps<SettingsFormTypes.EmployeeSettings>
                ) => {
                  if (!value) {
                    formikProps.setFieldValue(
                      FormFieldConstants.Settings.EmployeeSettings.TerminationReason,
                      undefined
                    );
                    formikProps.setFieldValue(
                      FormFieldConstants.Settings.EmployeeSettings.TerminationReasonName,
                      undefined
                    );
                  }
                },
              },
              terminationReasonName: {
                label: TranslationKeys.Settings.TerminationReason,
                descriptionText: TranslationKeys.Settings.TerminationReasonDescription,
                type: FormEnums.FormFieldType.AUTOCOMPLETE,
                hidden: (values: SettingsFormTypes.EmployeeSettings) =>
                  !salaryAvailable || !isWintSalarySettingsAvailable || !values.employmentEndDate,
                disabled: (values: SettingsFormTypes.EmployeeSettings) => values.canChangeTerminationReason === false,
                customMessageHandler: checkTerminationReasonLocked,
                extraProps: {
                  referenceFieldName: FormFieldConstants.Settings.EmployeeSettings.TerminationReason,
                  items: (
                    _value: string,
                    _formattedValue?: string,
                    formikProps?: FormikProps<SettingsFormTypes.EmployeeSettings>
                  ) => getTerminationReasonItems(formikProps?.values.terminationReason),
                } as AutoCompleteExtraProps<SettingsFormTypes.EmployeeSettings>,
              },
              taxAdjustment: {
                label: TranslationKeys.Settings.TaxAdjustment,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  suffix: "%",
                  decimalPoints: 0,
                },
                disabled: true,
                hideOptionalIndicator: isWintSalarySettingsAvailable,
                hidden: (values: SettingsFormTypes.EmployeeSettings) =>
                  !salaryAvailable ||
                  !isWintSalarySettingsAvailable ||
                  !values.employmentStartDate ||
                  values.wintSalarySpecificSettings?.taxPercentage == null,
              },
              taxTableName: {
                label: TranslationKeys.Settings.TaxTable,
                descriptionText: isWintSalarySettingsAvailable
                  ? TranslationKeys.Settings.TaxTableWintSalaryDescription
                  : TranslationKeys.Settings.TaxTableDescription,
                type: FormEnums.FormFieldType.AUTOCOMPLETE,
                hidden: (values: SettingsFormTypes.EmployeeSettings) =>
                  !salaryAvailable ||
                  (!isWintSalarySettingsAvailable && !values.salaryActive) ||
                  (isWintSalarySettingsAvailable && !values.employmentStartDate) ||
                  values.wintSalarySpecificSettings?.taxPercentage != null,
                disabled: isWintSalarySettingsAvailable,
                hideOptionalIndicator: isWintSalarySettingsAvailable,
                extraProps: {
                  referenceFieldName: FormFieldConstants.Settings.EmployeeSettings.TaxTable,
                  items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.TaxTableInfo).map((item) => ({
                    id: item,
                    value:
                      item === WintSuperkollAPIModel.TaxTableInfo.None
                        ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.None)
                        : item,
                  })),
                } as AutoCompleteExtraProps<SettingsFormTypes.EmployeeSettings>,
              },
              wintSalaryType: {
                label: TranslationKeys.Settings.SalaryType,
                descriptionText: TranslationKeys.Settings.SalaryTypeDescription,
                type: FormEnums.FormFieldType.CUSTOM,
                hidden: (values: SettingsFormTypes.EmployeeSettings) =>
                  !salaryAvailable || !isWintSalarySettingsAvailable || !values.employmentStartDate,
                customField: (
                  <SalaryTypeSelector
                    name={FormFieldConstants.Settings.EmployeeSettings.WintSalaryType}
                    availableSalaryTypes={props.availableSalaryTypes}
                  />
                ),
              },
              vacationConditions: {
                label: TranslationKeys.Settings.VacationDays,
                descriptionText: TranslationKeys.Settings.VacationDaysDescription,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  suffix: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Days),
                },
                hidden: (values: EmployeeSettingsWithTemp) => {
                  if (!salaryAvailable) return true;

                  // Always show for legacy system (not WintSalary)
                  if (!isWintSalarySettingsAvailable) return false;

                  // For WintSalary, show only if conditions are met
                  if (!values.employmentStartDate || !values.wintSalaryType) return true;
                  const selectedType = props.availableSalaryTypes.find((t) => t.name === values.wintSalaryType);
                  if (!selectedType) return true;
                  return (
                    (selectedType.optionallyRequiredFields &
                      WintSuperkollAPIModelWintSalary.WintSalaryOptionallyRequiredFields.VacationDays) ===
                    0
                  );
                },
              },
              tempVacationCompensation: {
                label: TranslationKeys.Settings.VacationCompensation,
                descriptionText: TranslationKeys.Settings.VacationCompensationDescription,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  suffix: "%",
                  decimalPoints: 1,
                  allowNegative: false,
                },
                hidden: (values: EmployeeSettingsWithTemp) => {
                  if (!salaryAvailable) return true;

                  // Hide for legacy system (not WintSalary)
                  if (!isWintSalarySettingsAvailable) return true;

                  // For WintSalary, show only if conditions are met
                  if (!values.employmentStartDate || !values.wintSalaryType) return true;
                  const selectedType = props.availableSalaryTypes.find((t) => t.name === values.wintSalaryType);
                  if (!selectedType) return true;
                  return (
                    (selectedType.optionallyRequiredFields &
                      WintSuperkollAPIModelWintSalary.WintSalaryOptionallyRequiredFields.VacationCompensation) ===
                    0
                  );
                },
              },
              ...(isWintSalarySettingsAvailable
                ? {
                    // WintSalary system - conditional fields based on salary type
                    monthlySalary: {
                      label: TranslationKeys.Settings.MonthlySalary,
                      descriptionText: TranslationKeys.Settings.MonthlySalaryDescription,
                      type: FormEnums.FormFieldType.NUMBER,
                      hidden: (values: EmployeeSettingsWithTemp) => {
                        if (!salaryAvailable || !values.employmentStartDate || !values.wintSalaryType) return true;
                        const selectedType = props.availableSalaryTypes.find((t) => t.name === values.wintSalaryType);
                        if (!selectedType) return true;
                        return (
                          (selectedType.optionallyRequiredFields &
                            WintSuperkollAPIModelWintSalary.WintSalaryOptionallyRequiredFields.MonthlyGrossSalary) ===
                          0
                        );
                      },
                      onChangeHandler: onSalaryChange,
                      customMessageHandler: checkActualSalary,
                    },
                    hourlySalary: {
                      label: TranslationKeys.Settings.HourlyWage,
                      descriptionText: TranslationKeys.Settings.HourlyWageDescription,
                      type: FormEnums.FormFieldType.NUMBER,
                      hidden: (values: EmployeeSettingsWithTemp) => {
                        if (!salaryAvailable || !values.employmentStartDate || !values.wintSalaryType) return true;
                        const selectedType = props.availableSalaryTypes.find((t) => t.name === values.wintSalaryType);
                        if (!selectedType) return true;
                        return (
                          (selectedType.optionallyRequiredFields &
                            WintSuperkollAPIModelWintSalary.WintSalaryOptionallyRequiredFields.HourlyGrossSalary) ===
                          0
                        );
                      },
                      onChangeHandler: onSalaryChange,
                    },
                  }
                : {
                    // Legacy system schedule
                    scheduleDescription: {
                      label: TranslationKeys.Settings.ScheduleDescription,
                      descriptionText: TranslationKeys.Settings.ScheduleDescriptionHelpText,
                      type: FormEnums.FormFieldType.TEXT,
                      hidden: (values: SettingsFormTypes.EmployeeSettings) => !values.salaryActive || !salaryAvailable,
                    },
                    // Legacy system single salary field
                    monthlySalary: {
                      label: TranslationKeys.Settings.MonthlySalaryBeforeTax,
                      descriptionText: TranslationKeys.Settings.MonthlySalaryBeforeTaxDescription,
                      type: FormEnums.FormFieldType.NUMBER,
                      hidden: (values: EmployeeSettingsWithTemp) => !values.salaryActive || !salaryAvailable,
                      onChangeHandler: onSalaryChange,
                    },
                  }),
              salaryChangeFrom: {
                label: TranslationKeys.Settings.ChangesApplyFrom,
                type: FormEnums.FormFieldType.DATE,
                hidden: (values: EmployeeSettingsWithTemp) => {
                  const monthlySalaryChanged =
                    !UtilsHelper.isEmpty(values.monthlySalary) && values.monthlySalary !== monthlySalary;
                  const hourlySalaryChanged =
                    !UtilsHelper.isEmpty(values.hourlySalary) &&
                    values.hourlySalary !== (defaultValues as EmployeeSettingsWithTemp)?.hourlySalary;
                  const anySalaryChanged = monthlySalaryChanged || hourlySalaryChanged;

                  return !anySalaryChanged || props.isCreate;
                },
              },
              salaryCostCenter: {
                label: " ",
                type: FormEnums.FormFieldType.DIMENSIONINPUTSELECTOR,
                hidden: (values: SettingsFormTypes.EmployeeSettings) =>
                  !salaryAvailable ||
                  !includeCostCenterDimensionToSalary ||
                  (!isWintSalarySettingsAvailable && !values.salaryActive) ||
                  (isWintSalarySettingsAvailable && !values.employmentStartDate),
                extraProps: {
                  type: DimensionEnums.DimensionCategory.All,
                  allowedTypes: [WintSuperkollAPIModel.DimensionCompatibilityType.CostCenter],
                },
              },
            },
          },
          {
            label: TranslationKeys.Settings.BankAccountInfo,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WCreditCard,
            hidden: (values: SettingsFormTypes.EmployeeSettings) =>
              isWintSalarySettingsAvailable && !values.employmentStartDate,
            innerFields: {
              bankAccountClearing: {
                label: TranslationKeys.Settings.ClearingNumber,
                descriptionText: TranslationKeys.Settings.ClearingNumberDescription,
                type: FormEnums.FormFieldType.TEXT,
              },
              bankAccount: {
                label: TranslationKeys.Settings.AccountNumber,
                descriptionText: TranslationKeys.Settings.AccountNumberDescription,
                type: FormEnums.FormFieldType.TEXT,
              },
            },
          },
          {
            label: TranslationKeys.Settings.WorkplaceAndPersonalInfo,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WCompany,
            hidden: (values: SettingsFormTypes.EmployeeSettings) =>
              isWintSalarySettingsAvailable && !values.employmentStartDate,
            innerFields: {
              employmentRate: {
                label: TranslationKeys.Settings.EmploymentRate,
                descriptionText: TranslationKeys.Settings.EmploymentRateDescription,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  decimalPoints: 2,
                  suffix: "%",
                },
                hidden: (values: SettingsFormTypes.EmployeeSettings) =>
                  !values.salaryActive || !salaryAvailable || isWintSalarySettingsAvailable,
              },
              employmentRateChangeFrom: {
                label: TranslationKeys.Settings.ChangesApplyFrom,
                type: FormEnums.FormFieldType.DATE,
                hidden: (values: SettingsFormTypes.EmployeeSettings) =>
                  values.employmentRate === employmentRate || props.isCreate || isWintSalarySettingsAvailable,
              },
              workplaceStreetAddress: {
                label: TranslationKeys.Settings.WorkPlaceStreetAddress,
                type: FormEnums.FormFieldType.TEXT,
                descriptionText: getWorkplaceDescriptionText(
                  TranslationKeys.Settings.WorkPlaceStreetAddressDescription,
                  "workplaceStreetAddress"
                ),
                hidden: !salaryAvailable,
              },
              workplaceCity: {
                label: TranslationKeys.Settings.WorkPlaceCity,
                type: FormEnums.FormFieldType.TEXT,
                descriptionText: getWorkplaceDescriptionText(
                  TranslationKeys.Settings.WorkPlaceCityDescription,
                  "workplaceCity"
                ),
                hidden: !salaryAvailable,
              },
              position: {
                label: TranslationKeys.Settings.Position,
                descriptionText: TranslationKeys.Settings.PositionDescription,
                type: FormEnums.FormFieldType.TEXT,
                hidden: !salaryAvailable,
              },
              occupationalCategorisation: {
                label: TranslationKeys.Settings.OccupationalCategorisation,
                descriptionText: TranslationKeys.Settings.OccupationalCategorisationDescription,
                type: FormEnums.FormFieldType.AUTOCOMPLETE,
                extraProps: {
                  referenceFieldName: FormFieldConstants.Settings.EmployeeSettings.OccupationalCategorisationId,
                  items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.EmployeeWorkType).map((category) => ({
                    id: category,
                    value: LocalizationHelperInstance.getFormattedMessage(
                      `Settings.OccupationalCategorisation_${category}`
                    ),
                  })),
                },
                hidden: !salaryAvailable,
              },
              usualWorkHours: {
                label: TranslationKeys.Settings.UsualWorkHours,
                descriptionText: TranslationKeys.Settings.UsualWorkHoursDescription,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  suffix: "h",
                  allowNegative: false,
                  decimalPoints: 2,
                },
                hidden: !salaryAvailable || isWintSalarySettingsAvailable,
              },
            },
          },
        ],
      },
      {
        label: TranslationKeys.Settings.WorkingHours,
        type: FormEnums.FormSectionType.WIZARDTAB,
        icon: IconDefinitionConstants.WTimer,
        className: cssExports.groupSection,
        hidden: (values: SettingsFormTypes.EmployeeSettings) =>
          !isWintSalarySettingsAvailable || !values.employmentStartDate,
        navKey: EmployeeConstants.NavigationKey.WorkingHours,
        innerFields: [
          {
            label: "",
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            inline: false,
            formGridLayout: { label: false, message: false },
            hidden: (values: SettingsFormTypes.EmployeeSettings) =>
              !values.futureSchedule || (Array.isArray(values.futureSchedule) && values.futureSchedule.length === 0),
            innerFields: {
              scheduledChanges: {
                label: "",
                type: FormEnums.FormFieldType.CUSTOM,
                customField: (
                  <ScheduledChangesWrapper
                    isWintSalarySettingsAvailable={isWintSalarySettingsAvailable}
                    scheduleViewMode={scheduleViewMode}
                    onScheduleViewModeChange={handleScheduleViewModeChange}
                    onScheduledChangesDetected={setHasScheduledChanges}
                  />
                ),
              },
            },
          },
          {
            label: TranslationKeys.Common.Generally,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WInfo,
            hidden: scheduleViewMode === "scheduled",
            innerFields: {
              [FormFieldConstants.Settings.EmployeeSettings.EmploymentRate]: {
                label: TranslationKeys.Settings.EmploymentRate,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  suffix: "%",
                  allowNegative: false,
                  decimalPoints: 2,
                },
                disabled: (values: SettingsFormTypes.EmployeeSettings) => {
                  return values.wintSalarySpecificSettings?.schedule?.futureSchedule != null;
                },
              },
              [FormFieldConstants.Settings.EmployeeSettings.UsualWorkHours]: {
                label: TranslationKeys.Settings.UsualWorkHours,
                descriptionText: TranslationKeys.Settings.UsualWorkHoursDescription,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  suffix: "h",
                  allowNegative: false,
                  decimalPoints: 2,
                },
                disabled: (values: SettingsFormTypes.EmployeeSettings) => {
                  return values.wintSalarySpecificSettings?.schedule?.futureSchedule != null;
                },
                customMessageHandler: checkUsualWorkHoursChangedWithoutRate,
              },
            },
          },
          {
            label: TranslationKeys.Common.Generally,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WInfo,
            hidden: scheduleViewMode !== "scheduled",
            innerFields: {
              futureEmploymentRate: {
                label: TranslationKeys.Settings.EmploymentRate,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  suffix: "%",
                  allowNegative: false,
                  decimalPoints: 2,
                },
              },
              futureUsualWorkHours: {
                label: TranslationKeys.Settings.UsualWorkHours,
                descriptionText: TranslationKeys.Settings.UsualWorkHoursDescription,
                type: FormEnums.FormFieldType.NUMBER,
                extraProps: {
                  suffix: "h",
                  allowNegative: false,
                  decimalPoints: 2,
                },
                customMessageHandler: checkFutureUsualWorkHoursChangedWithoutRate,
              },
            },
          },
          {
            label: TranslationKeys.Settings.Schedule,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WMonthCalendar,
            hidden: scheduleViewMode === "scheduled",
            inline: false,
            descriptionText: (
              <span
                dangerouslySetInnerHTML={{
                  __html: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Settings.ScheduleSectionDescription
                  ),
                }}
              />
            ),
            innerFields: {
              schedule: {
                label: "",
                type: FormEnums.FormFieldType.CUSTOM,
                customField: (
                  <ScheduleWithFormValues
                    scheduleViewMode={scheduleViewMode}
                    hasScheduledChanges={hasScheduledChanges}
                  />
                ),
              },
            },
          },
          {
            label: TranslationKeys.Settings.Schedule,
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            icon: IconDefinitionConstants.WMonthCalendar,
            hidden: scheduleViewMode !== "scheduled",
            inline: false,
            descriptionText: (
              <span
                dangerouslySetInnerHTML={{
                  __html: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Settings.ScheduleSectionDescription
                  ),
                }}
              />
            ),
            innerFields: {
              futureSchedule: {
                label: "",
                type: FormEnums.FormFieldType.CUSTOM,
                customField: (
                  <ScheduleWithFormValues
                    fieldName="futureSchedule"
                    employmentRateField="futureEmploymentRate"
                    usualWorkHoursField="futureUsualWorkHours"
                  />
                ),
              },
            },
          },
        ],
      },
      {
        label: TranslationKeys.Settings.EmployeeExpenseAccountSettings,
        type: FormEnums.FormSectionType.WIZARDTAB,
        icon: IconDefinitionConstants.WCosts2,
        className: cssExports.groupSection,
        hidden: !canCreate,
        navKey: EmployeeConstants.NavigationKey.OtherSettings,
        innerFields: [
          {
            label: "",
            type: FormEnums.FormSectionType.FIELDGROUPSECTION,
            descriptionText: TranslationKeys.Settings.BankAccountInfoDescription,
            innerFields: {
              additionalBankAccountClearing: {
                label: TranslationKeys.Settings.ClearingNumber,
                descriptionText: TranslationKeys.Settings.ClearingNumberForExpensesDescription,
                type: FormEnums.FormFieldType.TEXT,
              },
              additionalBankAccount: {
                label: TranslationKeys.Settings.AccountForExpenses,
                descriptionText: TranslationKeys.Settings.AccountForExpensesDescription,
                type: FormEnums.FormFieldType.TEXT,
              },
              assistants: {
                label: TranslationKeys.Settings.Assistants,
                descriptionText: TranslationKeys.Settings.AssistantsDescription,
                type: FormEnums.FormFieldType.MULTIPLEINPUT,
                hidden: !props.isAssistantsActive || props.isCreate,
                innerFields: {
                  name: {
                    label: TranslationKeys.Settings.Assistants,
                    type: FormEnums.FormFieldType.AUTOCOMPLETE,
                    icon: IconDefinitionConstants.WUser,
                    extraProps: {
                      items: props.assistants,
                      referenceFieldName: "id",
                    } as AutoCompleteExtraProps<SettingsFormTypes.EmployeeSettings>,
                  },
                },
              },
              businessUnitName: {
                label: TranslationKeys.Settings.BusinessUnit,
                descriptionText: TranslationKeys.Settings.BusinessUnitDescription,
                type: FormEnums.FormFieldType.AUTOCOMPLETE,
                icon: IconDefinitionConstants.WUser,
                hidden: !props.isCreate || !props.isBusinessUnitManagerWithSalaryRights,
                extraProps: {
                  items: props.getBusinessUnitsWithSalaryRights,
                  referenceFieldName: FormFieldConstants.Settings.EmployeeSettings.BusinessUnitId,
                } as AutoCompleteExtraProps<SettingsFormTypes.EmployeeSettings>,
              },
            },
          },
        ],
      },
    ];
  }, [
    scheduleViewMode,
    canCreate,
    salaryAvailable,
    isWintSalarySettingsAvailable,
    props.availableSalaryTypes,
    props.isCreate,
    props.isBusinessUnitManagerWithSalaryRights,
    props.getBusinessUnitsWithSalaryRights,
    props.userTypesEnabled,
    props.enabledUserTypes,
    monthlySalary,
    employmentRate,
    checkConcurrentVacationYear,
  ]);

  return (
    <GenericForm<EmployeeSettingsWithTemp>
      formRef={ref}
      inline
      {...props}
      wizardConfig={{
        showStepIcons: !props.isCreate,
        isSettingsWizard: true,
      }}
      fields={formFields}
    />
  );
};

export default forwardRef(EmployeeForm);
